import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon, QuoteIcon } from 'lucide-react';
const testimonials = [{
  name: 'Sarah M.',
  location: 'Houston, TX',
  context: 'Hurricane Season Survivor',
  rating: 5,
  text: "When Hurricane Beryl knocked out power for 6 days, we were the only family on our block who had everything organized. Insurance info, medical records, our kids' prescriptions — all right there in the binder. Our neighbors were scrambling. We were calm.",
  highlight: 'The only family on our block who had everything organized.'
}, {
  name: 'David & Karen R.',
  location: 'Nashville, TN',
  context: 'Parents of 3',
  rating: 5,
  text: 'My wife is a nurse and even she was impressed by how thorough the Medical Command Center section is. We filled it out on a Sunday afternoon — took maybe 90 minutes for our whole family of five. That peace of mind is worth a hundred times what we paid.',
  highlight: 'Peace of mind worth a hundred times what we paid.'
}, {
  name: 'James T.',
  location: 'Phoenix, AZ',
  context: 'Retired First Responder',
  rating: 5,
  text: "I spent 22 years as a firefighter. I've seen what happens to families who aren't prepared — the panic, the lost documents, the chaos. This binder is exactly what I've been telling people to put together for years, except someone finally did the hard work for you.",
  highlight: "Exactly what I've been telling people to put together for 22 years."
}, {
  name: 'Maria L.',
  location: 'Orlando, FL',
  context: 'Single Mom',
  rating: 5,
  text: 'As a single mom, the thought of something happening and my kids not having access to our important documents terrified me. I printed the binder, filled it out that same night, and put it in our go-bag. My 12-year-old even knows where it is. That matters.',
  highlight: 'I printed it, filled it out that same night.'
}, {
  name: 'Robert & Linda K.',
  location: 'Portland, OR',
  context: 'Wildfire Evacuation',
  rating: 5,
  text: "We had 15 minutes to evacuate during the wildfires last September. Fifteen minutes. Because of the 60-Second Grab List, we got out with everything that mattered — birth certificates, passports, insurance policies, medications. Everything. I can't imagine what we would have lost without this system.",
  highlight: '15 minutes to evacuate. We got out with everything that mattered.'
}, {
  name: 'Tom W.',
  location: 'Chicago, IL',
  context: 'IT Professional',
  rating: 5,
  text: 'I work in cybersecurity and I always assumed digital backups were enough. Then a ransomware attack locked me out of my own cloud storage for three weeks. Three weeks with no access to any family documents. This binder is my analog failsafe now. Everyone in tech should have one.',
  highlight: 'Locked out of my own cloud storage for three weeks.'
}];
function StarRating({
  count
}: {
  count: number;
}) {
  return <div className="flex gap-0.5" data-id="element-221">
      {Array.from({
      length: count
    }).map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-yellow-500 text-yellow-500" data-id="element-222" />)}
    </div>;
}
export function TestimonialsSection() {
  return <section className="bg-white py-20 px-4 border-b border-gray-200" data-id="element-223">
      <div className="max-w-7xl mx-auto" data-id="element-224">
        <div className="text-center mb-16" data-id="element-225">
          <span className="font-heading text-sm font-bold text-safety-red tracking-[0.2em] uppercase mb-2 block" data-id="element-226">
            Field Reports
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy" data-id="element-227">
            Families Who Were Ready When It Mattered
          </h2>
          <div className="w-24 h-1 bg-navy mx-auto mt-6" data-id="element-228"></div>
          <p className="font-body text-body-text mt-6 max-w-2xl mx-auto text-lg" data-id="element-229">
            Real stories from families who used The 72-Hour Emergency Binder
            when disaster struck.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-230">
          {testimonials.map((testimonial, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.08
        }} className="bg-light-gray border border-gray-200 p-6 flex flex-col relative" data-id="element-231">
              <div className="absolute top-0 left-0 w-full h-1 bg-navy" data-id="element-232"></div>

              <QuoteIcon className="w-8 h-8 text-navy/10 mb-4 flex-shrink-0" data-id="element-233" />

              <p className="font-body text-body-text leading-relaxed flex-1 mb-6" data-id="element-234">
                "{testimonial.text}"
              </p>

              <div className="bg-navy/5 border-l-4 border-safety-red px-3 py-2 mb-6" data-id="element-235">
                <p className="font-heading text-sm font-bold text-navy italic" data-id="element-236">
                  "{testimonial.highlight}"
                </p>
              </div>

              <div className="mt-auto pt-4 border-t border-gray-200 flex items-center justify-between" data-id="element-237">
                <div data-id="element-238">
                  <p className="font-heading font-bold text-navy text-sm uppercase tracking-wide" data-id="element-239">
                    {testimonial.name}
                  </p>
                  <p className="font-heading text-xs text-gray-500 uppercase tracking-widest" data-id="element-240">
                    {testimonial.location} · {testimonial.context}
                  </p>
                </div>
                <StarRating count={testimonial.rating} data-id="element-241" />
              </div>
            </motion.div>)}
        </div>

        {/* Summary stat bar */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="mt-12 bg-navy p-6 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 text-center" data-id="element-242">
          <div data-id="element-243">
            <p className="font-heading text-3xl font-bold text-white" data-id="element-244">
              12,400+
            </p>
            <p className="font-heading text-xs text-gray-400 uppercase tracking-widest mt-1" data-id="element-245">
              Families Protected
            </p>
          </div>
          <div className="hidden md:block w-px h-10 bg-gray-600" data-id="element-246"></div>
          <div data-id="element-247">
            <p className="font-heading text-3xl font-bold text-white" data-id="element-248">4.9/5</p>
            <p className="font-heading text-xs text-gray-400 uppercase tracking-widest mt-1" data-id="element-249">
              Average Rating
            </p>
          </div>
          <div className="hidden md:block w-px h-10 bg-gray-600" data-id="element-250"></div>
          <div data-id="element-251">
            <p className="font-heading text-3xl font-bold text-white" data-id="element-252">
              50 States
            </p>
            <p className="font-heading text-xs text-gray-400 uppercase tracking-widest mt-1" data-id="element-253">
              Across The U.S.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}